import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { joinSession, guestSearch, submitRequest, myRequests, GuestApiError } from '../../api/guest';
import type { SessionJoinInfo, GuestRequestView } from '../../api/guest';
import { RealtimeClient, type ConnectionState } from '../../api/realtime';
import {
  getDeviceId,
  getRememberedSingerName,
  setRememberedSingerName,
  getGuestPass,
  setGuestPass,
  clearGuestPass,
} from '../../lib/deviceId';
import { TurnstileWidget } from './TurnstileWidget';
import { useT } from '../../i18n/i18n.tsx';
import type { MessageKey } from '../../i18n/messages.en.ts';
import { describeRequestError, isDuplicateRequestError } from './requestErrors';
import {
  shouldRefetchMineOnFrame,
  mergeMineFrame,
  guestConnectionHint,
  shouldPollWhileDisconnected,
  planMineRefetch,
  INITIAL_MINE_REFETCH_SCHEDULER_STATE,
} from './guestRefetch';
import { isRequestDismissed, dismissRequest } from './dismissedRequest';
import { GuestSearch, type GuestFindMode } from './GuestSearch';
import { RequestSheet } from './RequestSheet';
import { QueuePositionCard, DISMISSIBLE_STATUSES } from './QueuePositionCard';
import { resolveQueueCardFocus, applyGuestNavIntent } from './guestResultsNav.ts';
import { TextField, Button, EmptyState, ErrorState, IconButton, InlineAlert, Spinner } from '../../ui';
import { getReachability } from '../../api/client';
import { useFullscreen } from '../../lib/useFullscreen.ts';
import { useConnectionHealth } from '../../app/useConnectionHealth';
import type { RequestMode, Song } from '../../api/types';
import './guest.css';

const SEARCH_DEBOUNCE_MS = 50; // AC-REQ-3 / mirrors features/library/timing.ts's SEARCH_DEBOUNCE_MS (MVP-209)
                                  // (not imported from there — that module is library-ui's, this
                                  // page has no other dependency on it, and the value is a named
                                  // SPEC constant, not a coupling either screen should own for the other).
const SEARCH_LIMIT = 30;
// MVP-274 R1: the browse fallback's page size. Smaller than `SEARCH_LIMIT` on purpose — a browse
// list is scrolled, not scanned for a known answer, and this is the one request on this page a
// customer makes without having asked for anything yet, over whatever the venue's wifi is.
const BROWSE_PAGE_SIZE = 20;

// P1 fix (round 6 critic), superseded by `planMineRefetch` (R11-P2-3): `shouldRefetchMineOnFrame`'s
// own doc comment explains why a refetch trigger exists at all — a burst of `public`-channel
// frames (or a queue/playback/rotation event) should only ever cost one `GET /api/requests/mine`
// call, not one per frame. The actual coalescing window is now `MINE_REFETCH_COALESCE_WINDOW_MS`
// (`guestRefetch.ts`) — see `scheduleMineRefetch` below for why a fixed per-frame debounce timer
// was not enough on its own.
// P1 fix (round 6 critic): AC-REQ-8's "keeps updating in the background" fallback for a device
// with a live request, on top of (never instead of) the realtime paths.
// MVP-11 #9: the server now pushes a device-scoped `request.mine` frame on both accept and reject
// (song-request controller's `accept`/`reject`), so this no longer runs unconditionally — see
// `shouldPollWhileDisconnected` (`guestRefetch.ts`) for the guard that arms it only while this
// device's own realtime connection is not actually delivering frames.
const MINE_POLL_INTERVAL_MS = 15_000;

// The exact sentence request-guard.js's `passcodeMatches` failure path sends from POST
// /api/requests (PASSCODE_INVALID) — reused verbatim here (as `guest.gate.wrongPasscode`, see
// `messages.en.ts`) because a wrong passcode at the GATE is not itself a thrown error
// (GET /api/sessions/join always returns 200 with `passcodeOk: false`), so `describeRequestError()`
// never sees it. One sentence, one place either way (DESIGN.md §7).

/** `session.updated`'s payload (session lifecycle's `afterUpdate`, published on the `public`
 * channel a guest's own SSE connection can actually see) — only the fields that shape, so a live
 * mode/venue/message/join-code change reaches an open phone within 500 ms (AC-REQ-5) without a
 * page reload, and a rotated join code invalidates this exact tab (AC-REQ-1). */
interface SessionUpdatedPayload {
  isActive: boolean;
  stageMessage: string | null;
  requestMode: RequestMode;
  joinCode: string;
  venue: string | null;
  name: string | null;
}

/**
 * DESIGN.md §9.3 `RequestPage` — the guest root (SPEC §6.7). Mounted by `App.tsx` for `/j/<code>`
 * BEFORE any auth/App-shell branching (same rule `StageRoot.tsx`'s own doc comment states for
 * `?view=stage`) — a phone reaching this page over cellular data has no operator session, no JWT,
 * and must never be offered one: no `AppHeader`, no `ConnectionIndicator` (which only ever talks
 * to the authenticated realtime client), no `LoginDialog`. Its OWN unauthenticated `RealtimeClient`
 * (`api/realtime.ts`'s additive `RealtimeOptions`) and its OWN fetch helper (`api/guest.ts`'s
 * `guestFetch`, never `apiFetch`) keep every byte on this page free of the operator's token even
 * if the same browser also has the console open in another tab — see that module's own doc
 * comment for exactly what leak that avoids.
 */
/** The minimal shape `sessionErrorCopy` below actually needs to render — a key+params pair for
 * each of `ErrorState`'s `title`/`description` slots. `describeRequestError()`'s richer
 * `RequestErrorCopy` (which also carries `tone`/`retryAfterSec`, unused on this particular gate)
 * satisfies this structurally, so both that call and the local `session.updated` "link expired"
 * object below can be assigned here without a cast. */
interface GateErrorCopy {
  titleKey: MessageKey;
  titleParams?: Record<string, string | number>;
  detailKey: MessageKey;
  detailParams?: Record<string, string | number>;
}

/**
 * MVP-269: the guest page had zero landmarks of any kind — it does not go through the app shell
 * (`App.tsx` returns it before any auth branch), so the `<main>` and skip link that task added
 * there never reached it. It is also the one surface in this product used by a stranger on their
 * own phone with their own assistive technology already running.
 *
 * A wrapper rather than six edited `<div>`s because `RequestPage` has six early returns — loading,
 * not found, closed, passcode, request-mode-closed, and the real page — and a landmark that exists
 * on five of them is not a landmark. This is also the only shape that keeps the root element type
 * identical across those transitions, which the `key="guest-search"` note further down was added to
 * protect: React reconciles the subtree in place instead of throwing away a field a guest is
 * mid-keystroke in.
 */
function GuestPage({ children, overlay }: { children: ReactNode; overlay?: ReactNode }) {
  const t = useT();
  return (
    <main className="guest-page" data-testid="request-page">
      {/* Hidden because the venue's own name is already the first thing on the page and is the
          answer a guest actually wants; this names the PAGE, for someone navigating by heading who
          cannot see that the rest of it is a search field and a keypad. */}
      <h1 className="visually-hidden">{t('guest.pageTitle')}</h1>
      {children}
      {/* MVP-483: the outage veil's slot. A prop on this wrapper rather than something each of the
          seven GuestPage returns below renders for itself — the cold-load spinner, the
          challenge gate, the passcode gate, the closed-session card and the search page are all
          equally capable of showing a customer a screen with nothing behind it, and a slot here is
          the only way one rule covers all of them. Last child so it paints over the page. */}
      {overlay}
    </main>
  );
}

export function RequestPage({ joinCode }: { joinCode: string }) {
  const t = useT();
  const [session, setSession] = useState<SessionJoinInfo | null>(null);
  const [sessionErrorCopy, setSessionErrorCopy] = useState<GateErrorCopy | null>(null);
  // Session controller's `join()` (session/controllers/session.js) runs `evaluateSessionAvailability`
  // BEFORE it ever builds a response body: a closed session gets a 403 `REQUESTS_CLOSED` — it never
  // comes back as a 200 with `requestMode: 'closed'` in the payload. That means the cold-load "closed"
  // case has no venue/name/message fields to show (the server never got that far), unlike the LIVE
  // "closed" case below (a `session.updated` frame arriving after this page already has a full
  // `SessionJoinInfo`) — hence a separate bit of state rather than reusing `session.requestMode`.
  const [closedMessage, setClosedMessage] = useState<string | null>(null);
  const [passcode, setPasscode] = useState('');
  const [passcodeTried, setPasscodeTried] = useState(false);
  const [passcodeSubmitting, setPasscodeSubmitting] = useState(false);
  // MVP-469: wrong passcodes are charged against a per-device and per-address budget, because
  // `passcodeOk` is otherwise an unlimited oracle for a four-digit PIN. True once this phone has
  // spent it; cleared by the next answer the server is willing to give.
  const [passcodeBlocked, setPasscodeBlocked] = useState(false);

  // MVP-471: what this phone got for passing Turnstile, read from storage so a reload (or a second
  // tab) never re-challenges the same guest. Null on every deployment that has not configured
  // Turnstile, where nothing ever asks for it.
  const [guestPass, setGuestPassState] = useState<string | null>(() => getGuestPass());
  // The widget could not load at all — blocked network, content blocker. Says so instead of
  // leaving an empty box where a challenge should be, which is a dead end with nothing to read.
  const [challengeUnavailable, setChallengeUnavailable] = useState(false);
  const [challengeSubmitting, setChallengeSubmitting] = useState(false);

  // MVP-274 R1: two doors into one result list — the catalogue code (the default; it is what the
  // venue's printed book is ordered by and what every customer holding that book already has) and
  // the free-text search that used to be the only way in. Separate state per door so switching back
  // and forth does not destroy what was typed behind the other one.
  const [mode, setMode] = useState<GuestFindMode>('code');
  const [query, setQuery] = useState('');
  const [code, setCode] = useState('');
  const [songs, setSongs] = useState<Song[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchErrorMessage, setSearchErrorMessage] = useState<string | null>(null);
  // Browse state (code mode, nothing typed): the whole catalogue in code order, one page at a time.
  // `browsing` is what tells `GuestSearch` this list is a catalogue and not a result set.
  const [browsing, setBrowsing] = useState(false);
  const [browseCursor, setBrowseCursor] = useState<string | null>(null);
  const [browseHasMore, setBrowseHasMore] = useState(false);
  const [browseLoadingMore, setBrowseLoadingMore] = useState(false);
  /** MVP-451: a failed NEXT page is not a failed search. It used to write `searchErrorMessage`,
   * which `GuestSearch` renders instead of the list — so one flaky request at song 40 replaced 40
   * songs the customer was reading with an alert. Its own slot, rendered under the list, and the
   * thing that stops the sentinel asking again until they press Retry (MVP-230's rule). */
  const [browseMoreError, setBrowseMoreError] = useState<string | null>(null);
  /** MVP-623: the search effect aborts its own request and nothing else, but `loadMoreBrowse`
   * writes the SAME list from outside it — so a page 2 still in flight when the customer taps a
   * code landed 40 catalogue songs underneath their one match and turned `browseHasMore` back on.
   * `loadMoreBrowse` has no effect body to be cleaned up, so it gets the generation instead: the
   * search effect bumps this every time it takes ownership of the list, and a response whose
   * generation has moved on is dropped whole. */
  const browseGenerationRef = useRef(0);

  const [sheetSong, setSheetSong] = useState<Song | null>(null);
  const [singerName, setSingerName] = useState<string>(() => getRememberedSingerName() ?? '');
  const [keyShift, setKeyShift] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitErrorCopy, setSubmitErrorCopy] = useState<ReturnType<typeof describeRequestError> | null>(null);

  const [activeRequest, setActiveRequest] = useState<GuestRequestView | null>(null);
  // R10-P2-3(a) fix: `request.etaSec` is a snapshot computed once, server-side, at the moment this
  // payload was built — it never counts down on its own. Recording when THIS device received it
  // lets `QueuePositionCard` derive a genuinely ticking display (`computeDisplayEtaSec`) instead of
  // showing a frozen "about 1:14 to go" for the entire wait. Updated in lockstep with every
  // `setActiveRequest` call below, including the merged-frame path.
  const [activeRequestFetchedAt, setActiveRequestFetchedAt] = useState<number | null>(null);
  // MVP-491. A keyboard-only guest presses Enter in the request sheet and, before this, that was
  // the end of the road: the sheet's focus trap returns focus to the row that opened it
  // (`ui/useFocusTrap.ts:86`), and the queue-position card announcing what actually happened had
  // neither a tab stop nor a reason for a screen reader to read it. These two carry focus the rest
  // of the way. The flag is the same shape MVP-490's `pendingSearchFocus` takes and for the same
  // reason: the side that KNOWS focus should move is not the side that can move it yet.
  const queueCardRef = useRef<HTMLDivElement | null>(null);
  const pendingQueueFocusRef = useRef(false);
  // MVP-497 Commit B: browser fullscreen only on this surface — no `html:fullscreen` rule hides any
  // guest chrome, unlike Library's half of the task. The two things a guest would lose are the
  // venue's own name (the answer they most often came for) and `session.message`, copy the venue
  // wrote for its own customers; ~30px on an 812px phone is not worth suppressing either. The real
  // beneficiary is a room PC, where this reclaims the omnibox and tab strip. On an iPhone the
  // button does not render at all: Safari exposes `requestFullscreen` on `<video>` only, so
  // `document.fullscreenEnabled` is false and `useFullscreen`'s `supported` — read from exactly
  // that property — comes back false, which is the gate on the JSX below.
  const fullscreen = useFullscreen();
  // R9-P1-D fix (round 9 critic, reproduced live): drives the connection hint on
  // `QueuePositionCard` (MVP-11 #16: `guestRefetch.ts`'s `guestConnectionHint`, reusing Package
  // E's `connectionLabel` copy) — a frozen-looking card during a dead SSE stream must read as
  // "still working, just slower", not silent failure.
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  // MVP-483: the client itself, not just its state — `useConnectionHealth` subscribes to it. Held
  // in state rather than a ref so the hook re-subscribes when the effect below builds a new one
  // (a join-code change, or a session that came back). `null` until there is a session to connect
  // for, which is a supported argument: an outage before the join call ever succeeds is still an
  // outage, and REST reachability alone is enough to say so.
  const [realtimeClientInstance, setRealtimeClientInstance] = useState<RealtimeClient | null>(null);
  // MVP-498 slice 2. Bumped by a `session.roomMoved` frame; it is in the realtime effect's deps, so
  // bumping it tears the `RealtimeClient` down and builds a fresh one.
  //
  // A counter rather than a boolean because a move is repeatable — a group upgraded to the VIP room
  // and then moved again to the big room must reconnect twice, and a boolean that is already `true`
  // would not re-run the effect the second time.
  const [realtimeEpoch, setRealtimeEpoch] = useState(0);
  // Owner decision 3: the phone SAYS something while it reconnects rather than going quiet. Cleared
  // by the new connection's own `hello` frame, so the line is on screen for exactly as long as the
  // reconnect actually takes.
  const [roomMoved, setRoomMoved] = useState(false);
  // MVP-483: the veil's retry button — see `handleOutageRetry` below.
  const [outageRetrying, setOutageRetrying] = useState(false);
  const [outageRetryFailed, setOutageRetryFailed] = useState(false);

  const deviceIdRef = useRef<string | undefined>(undefined);
  if (!deviceIdRef.current) deviceIdRef.current = getDeviceId();
  const deviceId = deviceIdRef.current;

  // AC-REQ-9 "retry on reconnect with idempotencyKey so duplicates are impossible": reused across
  // every retry of the SAME attempt (repeated taps of Submit while the sheet stays open after a
  // failure), minted fresh only when a NEW attempt starts — a successful submit (handleSubmit) or
  // opening the sheet for a (possibly different) song (openSheetFor).
  const idempotencyKeyRef = useRef<string | undefined>(undefined);
  if (!idempotencyKeyRef.current) idempotencyKeyRef.current = crypto.randomUUID();

  const hasSession = session !== null;
  // MVP-471: a challenge stands in front of everything else this page does — including the
  // background catalogue browse, which is why it is folded into `ready` rather than only checked
  // at render time. A phone that has not proved it is a phone should not be pulling the catalogue.
  const needsChallenge = session !== null && session.turnstile.required && !guestPass;
  const ready =
    session !== null && !needsChallenge && !(session.passcodeRequired && !session.passcodeOk) && session.requestMode !== 'closed';

  // Shared by the visibilitychange refetch, the 15s poll fallback, the SSE-(re)connect refetch, and
  // the realtime-frame-triggered debounce below — one place that decides "go fetch this device's
  // newest request", reused by every trigger that can fire it rather than four copies of the same
  // `myRequests(...).then(...)`.
  //
  // R9-P1-D fix (round 9 critic, reproduced live): this used to bail out entirely whenever
  // `document.visibilityState !== 'visible'` — confirmed live, that gate produced ZERO calls to
  // `GET /api/requests/mine` for an entire guest session (the exact defect report: "0 calls...
  // during a whole session"), because the automation/embedding context this was tested in never
  // reported `'visible'` even though the page was genuinely on screen the whole time. There is no
  // way to distinguish "hidden because the OS actually backgrounded this tab" from "hidden because
  // the embedding context never sets this correctly" from inside this component, and the whole
  // point of the 15s poll fallback below is to keep working when nothing else can be trusted — so
  // it (and the reconnect/frame-triggered paths, which share this same function) no longer trusts
  // `visibilityState` at all. The one-off mount call and the `visibilitychange` listener below are
  // cheap enough to fire unconditionally in the rare case a background poll costs a wasted request.
  // R11-P2-3 fix (round 11 critic, reproduced live): every trigger that used to call `refetchMine`
  // (or, for `hello`/matching frames, schedule its OWN `setTimeout`) now routes through the
  // `scheduleMineRefetch` scheduler below instead — see `guestRefetch.ts`'s `planMineRefetch` doc
  // comment for exactly why a per-frame 500ms debounce timer still let one queue change cost 2
  // network calls (frames landing ~2s apart each got a fresh timer). Refs, not state, deliberately
  // — this is scheduling bookkeeping, not anything that should ever trigger a re-render.
  const mineSchedulerStateRef = useRef(INITIAL_MINE_REFETCH_SCHEDULER_STATE);
  const mineTrailingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runMineRefetch = useCallback(() => {
    mineSchedulerStateRef.current = { ...mineSchedulerStateRef.current, fetchInFlight: true };
    // MVP-622: scoped to tonight's session — `list[0]` below is only the right row if the server
    // is not also answering with rows from a night this phone was here for weeks ago.
    myRequests(deviceId, joinCode)
      .then((list) => {
        // MVP-11 #32: the newest request is not necessarily the one to show — a guest who already
        // dismissed this exact (terminal) request must not have it reappear on the next refetch/
        // reload just because the server still reports it as this device's newest row.
        // MVP-11 #3: only skip while the status is still one of the dismissible terminal ones —
        // the same request can go terminal -> live again (KJ removes it, guest dismisses the
        // "Removed" card, KJ hits Undo), and a stale dismissed id must not hide a live card.
        const newest = list[0];
        if (newest && !(DISMISSIBLE_STATUSES.has(newest.status) && isRequestDismissed(newest.requestId))) {
          setActiveRequest(newest);
          setActiveRequestFetchedAt(Date.now());
        }
      })
      .catch(() => {
        /* best-effort — the SSE connection is the primary path, this just recovers state */
      })
      .finally(() => {
        mineSchedulerStateRef.current = { ...mineSchedulerStateRef.current, fetchInFlight: false, lastCompletedAt: Date.now() };
      });
  }, [deviceId, joinCode]);

  const scheduleMineRefetch = useCallback(() => {
    const plan = planMineRefetch(mineSchedulerStateRef.current, Date.now());
    if (plan.action === 'fetch-now') {
      runMineRefetch();
    } else if (plan.action === 'schedule-trailing') {
      mineSchedulerStateRef.current = { ...mineSchedulerStateRef.current, trailingScheduled: true };
      if (mineTrailingTimerRef.current) clearTimeout(mineTrailingTimerRef.current);
      mineTrailingTimerRef.current = setTimeout(() => {
        mineSchedulerStateRef.current = { ...mineSchedulerStateRef.current, trailingScheduled: false };
        runMineRefetch();
      }, plan.delayMs);
    }
    // 'noop': a fetch is already in flight or a trailing one is already queued — this trigger
    // needs nothing further, the queued/in-flight fetch already covers it.
  }, [runMineRefetch]);

  // Clears any trailing fetch this scheduler queued if the page unmounts (e.g. navigating away)
  // before it fires — an unmounted component's `setActiveRequest` would otherwise be a no-op React
  // warning at best, a stray network call at worst.
  useEffect(() => {
    return () => {
      if (mineTrailingTimerRef.current) clearTimeout(mineTrailingTimerRef.current);
    };
  }, []);

  /**
   * One join call, whatever this phone currently has to prove. `pass` defaults to whatever is in
   * state but is an explicit parameter so the CHALLENGE_REQUIRED branch below can retry with
   * `null` in the same tick — a `setState` does not change the value this closure already
   * captured, and a retry that sent the rejected pass again would loop.
   */
  async function loadSession(withPasscode?: string, turnstileToken?: string, pass: string | null = guestPass) {
    try {
      // MVP-469: `deviceId` is only read when the POST form is used, and it is what gives this
      // phone its own wrong-PIN allowance rather than sharing one with every other phone behind
      // the venue's single address.
      // MVP-471: the pass rides along on every join too. Presenting one is what lets the passcode
      // gate (which POSTs here on each attempt) and a plain reload skip the widget — see
      // `api/guest.ts`'s `JoinOptions`.
      const info = await joinSession(joinCode, { passcode: withPasscode, deviceId, turnstileToken, guestPass: pass });
      setSession(info);
      setSessionErrorCopy(null);
      setClosedMessage(null);
      setPasscodeBlocked(false);
      // A fresh pass arrives on the POST that just passed a challenge, and again whenever an
      // existing one is re-presented — store the newest, so the twelve-hour clock restarts from
      // the last time this phone actually talked to the server rather than from its first join.
      if (info.guestPass) {
        setGuestPass(info.guestPass);
        setGuestPassState(info.guestPass);
      }
    } catch (err) {
      // MVP-471: the pass this phone holds is no good any more (expired, or the venue rotated its
      // Turnstile secret). Drop it and let the widget come back — retrying the same string would
      // fail forever, and the guest would sit on a gate with no way through.
      if (err instanceof GuestApiError && (err.code === 'CHALLENGE_REQUIRED' || err.code === 'CHALLENGE_FAILED')) {
        clearGuestPass();
        setGuestPassState(null);
        // Retry WITHOUT it, so the page still learns the venue name, the mode and the site key it
        // needs to draw the widget. Skipping this would leave a phone whose stored pass went stale
        // staring at the loading state with nothing to act on.
        if (pass) await loadSession(withPasscode, undefined, null);
        return;
      }
      // MVP-469: a 429 from the passcode POST is the ONE failure here that must not throw the
      // guest off the gate. Clearing `session` would replace the passcode field with a full-page
      // error and leave them no way back to it, which is a harsh answer to somebody who simply
      // mistyped a PIN they were told out loud. Keep the gate, say to wait.
      if (err instanceof GuestApiError && err.code === 'RATE_LIMITED') {
        setPasscodeBlocked(true);
        return;
      }
      setSession(null);
      if (err instanceof GuestApiError && err.code === 'REQUESTS_CLOSED') {
        // MVP-371: `request-guard.js`'s own sentence is English; the guest page is Thai by default.
        setClosedMessage(t('server.requestsClosed'));
        setSessionErrorCopy(null);
        return;
      }
      setClosedMessage(null);
      setSessionErrorCopy(describeRequestError(err));
    }
  }

  // P2 fix (round 3): StrictMode (main.tsx) deliberately double-invokes an effect with no cleanup
  // on mount — without this guard `loadSession()` fired the SAME `GET /api/sessions/join` twice on
  // every load (confirmed live: two identical requests, both 200, or both 404 for a bad code).
  // Guarding on the code itself (not a plain "ran once" boolean) keeps the effect's own documented
  // behaviour: a genuinely new `joinCode` from the route resolver still starts a fresh join.
  const joinStartedForCodeRef = useRef<string | null>(null);
  useEffect(() => {
    if (joinStartedForCodeRef.current === joinCode) return;
    joinStartedForCodeRef.current = joinCode;
    void loadSession();
    // Only ever re-runs if the route hands this component a different code — Task A's resolver
    // remounts on navigation in practice, but this guards the same-component-different-prop case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinCode]);

  // Live session updates (AC-REQ-1 join-code rotation, AC-REQ-5 mode changes within 500ms) plus
  // this device's own request status (AC-REQ-4/8). One connection for both — SPEC §4.4's "one
  // realtime client" rule applies to the guest page just as much as the operator console. Starts
  // as soon as a session has been found at all (even mid passcode-gate/closed), not only once
  // `ready` — a KJ re-opening a closed session, or fixing a rotated code, should reach every phone
  // that ever loaded this page, not only the ones already past the gate.
  useEffect(() => {
    if (!hasSession) return;
    // MVP-472 follow-up 4: the join code goes up with the connection so the server can scope this
    // phone to the room it is sitting in — without it the hub has no way to tell room 3's guest
    // from room 5's, and every room's `queue.changed` reaches every phone in the shop.
    const client = new RealtimeClient({ channels: ['public', 'requests'], deviceId, joinCode, useAuth: false });
    // R9-P1-D fix: drives the connection hint (MVP-11 #16: `guestRefetch.ts`'s
    // `guestConnectionHint`) — tracked independently of the frame handling below so the hint
    // reflects the connection's OWN state, not whatever this device's request happens to be doing.
    const unsubscribeState = client.onStateChange(setConnectionState);
    setRealtimeClientInstance(client); // MVP-483: see `realtimeClientInstance`'s own comment
    const unsubscribe = client.onFrame((frame) => {
      // R9-P1-D fix: a `hello` frame is this connection's own signal that it just (re)connected
      // (SPEC's hello frame, sent once per successful `connectOnce()`) — schedule a refetch rather
      // than waiting on a poll, so a device that just came back from a dead stream (the round-9
      // critic's "reconnects returned 403 for 4+ minutes" repro) sees its real status close to the
      // instant the connection recovers. Routed through `scheduleMineRefetch` (R11-P2-3) like every
      // other trigger below, so a `hello` landing right after a `request.mine` frame still only
      // costs one network call, not two.
      if (frame.event === 'hello') {
        scheduleMineRefetch();
        // MVP-498 slice 2: this connection is up, so the room-move notice has served its purpose.
        setRoomMoved(false);
      }
      // MVP-498 slice 2 — THE ROOM MOVE, and this branch sits ABOVE the `session.updated` handling
      // below on purpose.
      //
      // The group was walked to another room and their whole night went with them: one row update on
      // `session.roomRef`, so every request, queue entry and play-history row followed. The join code
      // did NOT change — it lives on the session, so it travelled too — and that is exactly what
      // makes this a silent failure without this branch.
      //
      // `client.roomId` is computed ONCE at subscribe time from the join code (the realtime
      // controller) and never recomputed, and the hub's room filter fails CLOSED. So after a move,
      // every frame for the night's new room is dropped for this connection, while the guest's only
      // bounce condition (`!isActive || joinCode !== joinCode`, just below) stays false on BOTH halves
      // — the session is still active and the code is still the same. The phone shows a queue that
      // never updates again, with no error at all.
      //
      // The server publishes this frame scoped to the room being LEFT, which is the only scope this
      // connection can still receive anything on. Bumping the epoch rebuilds the `RealtimeClient`,
      // which re-runs the subscribe and gets the room resolved a second time.
      //
      // `setSession(null)` is NEVER called here, and that is the point of the ordering: a move must
      // not throw the phone back to the join screen. The guest has not lost anything — they are
      // holding the same code, in a nicer room.
      if (frame.channel === 'public' && frame.event === 'session.roomMoved') {
        setRoomMoved(true);
        setRealtimeEpoch((n) => n + 1);
        return;
      }
      if (frame.channel === 'requests' && frame.event === 'request.mine') {
        // R8-P1-2 (round 8 critic): merge, don't replace — a `request.mine` push is not guaranteed
        // to carry `position`/`etaSec` even while still genuinely `accepted` (see
        // `mergeMineFrame`'s doc comment), so blindly overwriting `activeRequest` with this payload
        // could regress a live "#N in line" card to the "ask the KJ" fallback for no real reason.
        // `shouldRefetchMineOnFrame` below still schedules a coalesced `GET /api/requests/mine` for
        // EVERY `request.mine` frame too, so this merge only ever has to hold the line until that
        // authoritative refetch lands moments later.
        setActiveRequest((prev) => mergeMineFrame(prev, frame.payload as GuestRequestView));
        setActiveRequestFetchedAt(Date.now());
      }
      if (frame.channel === 'public' && frame.event === 'session.updated') {
        const payload = frame.payload as SessionUpdatedPayload;
        if (!payload.isActive || payload.joinCode !== joinCode) {
          // This code no longer points at a live session (rotated, or the session ended).
          setSession(null);
          setSessionErrorCopy({ titleKey: 'guest.gate.linkExpiredTitle', detailKey: 'guest.gate.linkExpiredDetail' });
          return;
        }
        setSession((prev) =>
          prev
            ? { ...prev, requestMode: payload.requestMode, venue: payload.venue, name: payload.name, message: payload.stageMessage || null }
            : prev
        );
      }
      if (shouldRefetchMineOnFrame(frame)) {
        scheduleMineRefetch();
      }
    });
    client.start();
    return () => {
      unsubscribeState();
      unsubscribe();
      client.stop();
      setConnectionState('connecting');
      setRealtimeClientInstance(null);
    };
    // `realtimeEpoch` (MVP-498 slice 2) is a dep so that a room move rebuilds the client: the
    // server resolves a guest's room from the join code at SUBSCRIBE time and never again, so a new
    // connection is the only way to be scoped to the room the night is now in.
  }, [hasSession, deviceId, joinCode, realtimeEpoch, scheduleMineRefetch]);

  // AC-REQ-8: "keeps updating in the background and on returning to the tab" — recovers whatever
  // this device's newest request is (there may be one from earlier tonight even before this page
  // instance ever calls submitRequest itself, e.g. a reload) and refreshes it whenever the tab
  // becomes visible again, since a phone's SSE connection is routinely suspended while backgrounded.
  useEffect(() => {
    if (!hasSession) return;
    scheduleMineRefetch();
    document.addEventListener('visibilitychange', scheduleMineRefetch);
    return () => document.removeEventListener('visibilitychange', scheduleMineRefetch);
  }, [hasSession, scheduleMineRefetch]);

  // P1 fix (round 6 critic): 15s poll fallback while THIS device has a live request — see
  // `MINE_POLL_INTERVAL_MS`'s doc comment. Gated on a boolean (not `activeRequest` itself) so a
  // fresh `request.mine`/refetch payload — which arrives as a brand-new object every time — does
  // not tear down and restart this interval on every update.
  // MVP-11 #9: every accept/reject this device could hit now has a live `request.mine` push
  // behind it (see `shouldPollWhileDisconnected`'s doc comment) — this fallback only needs to run
  // while the realtime connection genuinely is not delivering frames, same rule Package E applies
  // to the console's own polls.
  const hasActiveRequest = activeRequest !== null;
  useEffect(() => {
    if (!hasSession || !hasActiveRequest || !shouldPollWhileDisconnected(connectionState)) return;
    const id = setInterval(scheduleMineRefetch, MINE_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasSession, hasActiveRequest, connectionState, scheduleMineRefetch]);

  // The guest's one read (AC-REQ-3), debounced and cancellable, serving both doors and the browse
  // fallback. Only runs once genuinely usable (`ready`) — no point racing the network while the
  // passcode gate or a closed-mode message is what's actually on screen.
  //
  // MVP-274 R1: three cases, one effect, because all three produce the same list.
  //   code mode + digits   -> search for the code. `catalog_id` is folded into `searchText` and an
  //                           exact match is the TOP ranking tier (`ranking.js`'s `buildTierCase`),
  //                           so the typed number's own song comes back first with no new endpoint.
  //   code mode + nothing  -> BROWSE: the catalogue in code order, the fallback for a customer with
  //                           no book and no title in mind. Paginated by `browseCursor`.
  //   search mode          -> free text, exactly as before. Nothing typed still shows the plain
  //                           prompt rather than firing a request for it.
  useEffect(() => {
    if (!ready) return;
    const term = (mode === 'code' ? code : query).trim();
    const shouldBrowse = mode === 'code' && !term;
    // This effect owns the list from here on; anything `loadMoreBrowse` has in flight belongs to
    // the list it is about to replace. `browseMoreError` is reset with the rest of the browse group
    // for the same reason: the error belongs to the list, so it dies with the list.
    browseGenerationRef.current += 1;

    if (!term && !shouldBrowse) {
      setSongs([]);
      setBrowsing(false);
      setBrowseCursor(null);
      setBrowseHasMore(false);
      setBrowseMoreError(null);
      setSearchLoading(false);
      setSearchErrorMessage(null);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      setSearchLoading(true);
      const params = shouldBrowse
        ? { q: '', limit: BROWSE_PAGE_SIZE, sort: 'code' as const, signal: controller.signal }
        : { q: term, limit: SEARCH_LIMIT, signal: controller.signal };
      guestSearch(params)
        .then((res) => {
          setSongs(res.songs);
          setBrowsing(shouldBrowse);
          setBrowseCursor(shouldBrowse ? res.cursor : null);
          setBrowseHasMore(shouldBrowse ? res.hasMore : false);
          setBrowseMoreError(null);
          setSearchErrorMessage(null);
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          setSongs([]);
          setBrowsing(false);
          setBrowseCursor(null);
          setBrowseHasMore(false);
          setBrowseMoreError(null);
          const errCopy = describeRequestError(err);
          setSearchErrorMessage(t(errCopy.detailKey, errCopy.detailParams));
        })
        .finally(() => {
          if (!controller.signal.aborted) setSearchLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, mode, query, code]);

  // Browse only. Appends rather than replaces, and is deliberately outside the effect above: none of
  // that effect's inputs change when a customer asks for another page, so a "show more" must not be
  // able to re-run (or be cancelled by) the debounce that produced page 1.
  const loadMoreBrowse = useCallback(() => {
    if (!browseCursor || browseLoadingMore) return;
    setBrowseLoadingMore(true);
    setBrowseMoreError(null);
    // The list this page is a continuation OF. If the search effect has taken the list back since
    // (a typed code, a mode switch), every one of these writes would land on somebody else's list:
    // songs appended under a single code match, and `browseHasMore` switched back on so the
    // sentinel keeps paging the catalogue from a cursor that no longer describes what is on screen.
    const generation = browseGenerationRef.current;
    guestSearch({ q: '', limit: BROWSE_PAGE_SIZE, sort: 'code', cursor: browseCursor })
      .then((res) => {
        if (browseGenerationRef.current !== generation) return;
        setSongs((prev) => [...prev, ...res.songs]);
        setBrowseCursor(res.cursor);
        setBrowseHasMore(res.hasMore);
      })
      .catch((err) => {
        if (browseGenerationRef.current !== generation) return;
        const errCopy = describeRequestError(err);
        setBrowseMoreError(t(errCopy.detailKey, errCopy.detailParams));
      })
      // Not generation-guarded, unlike the two above: `browseLoadingMore` is the re-entry lock, so
      // a dropped response that never released it would wedge the sentinel for good.
      .finally(() => setBrowseLoadingMore(false));
  }, [browseCursor, browseLoadingMore, t]);

  function openSheetFor(song: Song) {
    setSheetSong(song);
    setSubmitErrorCopy(null);
    setKeyShift(0);
    idempotencyKeyRef.current = crypto.randomUUID();
  }

  function closeSheet() {
    if (submitting) return;
    setSheetSong(null);
  }

  async function handlePasscodeSubmit(e: FormEvent) {
    e.preventDefault();
    setPasscodeSubmitting(true);
    setPasscodeTried(true);
    await loadSession(passcode.trim());
    setPasscodeSubmitting(false);
  }

  /**
   * MVP-471: the widget produced a token. It is worth nothing until the server checks it, so the
   * only thing to do with it is join again and see whether a pass comes back. Sent with `null` for
   * the stored pass on purpose — this path is only reached when there was no usable one.
   */
  async function handleChallengeToken(token: string) {
    setChallengeUnavailable(false);
    setChallengeSubmitting(true);
    await loadSession(undefined, token, null);
    setChallengeSubmitting(false);
  }

  async function handleSubmit() {
    if (!sheetSong) return;
    const name = singerName.trim();
    if (!name) return;
    setSubmitting(true);
    setSubmitErrorCopy(null);
    try {
      const result = await submitRequest({
        joinCode,
        song: sheetSong.documentId,
        singerName: name,
        deviceId,
        keyShift,
        passcode: session?.passcodeRequired ? passcode : undefined,
        // MVP-471: proof of the one challenge this phone met at the join gate. The server checks
        // it locally per request, so a guest queues song after song without ever seeing a second
        // widget — a captcha per song, on a phone, in a dark room, would be worse than the abuse.
        guestPass,
        idempotencyKey: idempotencyKeyRef.current!,
      });
      setRememberedSingerName(name);
      // MVP-491: set only on this branch. The duplicate-request branch below also calls
      // `setActiveRequest`, but it leaves the sheet OPEN with an error the guest still has to read.
      pendingQueueFocusRef.current = true;
      setActiveRequest(result);
      setActiveRequestFetchedAt(Date.now());
      idempotencyKeyRef.current = crypto.randomUUID();
      setSheetSong(null);
    } catch (err) {
      // R11-P2-2 fix (round 11 critic, reproduced live): a 409 DUPLICATE_REQUEST used to be treated
      // as a silent soft success — the sheet closed with `activeRequest` swapped in from `err.data`
      // and no visible sign anything happened. Live repro: re-requesting a song already in this
      // device's queue just closed the sheet with nothing to show for it. This is a real mistake to
      // surface, not a success: the sheet now stays open with the same copy any other rejected
      // submit gets (`describeRequestError` — "Already on the list" / the server's own "You already
      // asked for this song tonight."), via `isDuplicateRequestError` below, while a background
      // refetch (`scheduleMineRefetch`) keeps this device's own request card in sync with whatever
      // the server says its live request actually is.
      setSubmitErrorCopy(describeRequestError(err));
      // MVP-471: the pass went stale mid-night (twelve hours, or the venue rotated its secret).
      // Dropping it puts the widget back on screen, which is the only thing that can fix it —
      // the sheet keeps what was typed, so the song is still one tap away afterwards.
      if (err instanceof GuestApiError && err.code === 'CHALLENGE_REQUIRED') {
        clearGuestPass();
        setGuestPassState(null);
      }
      if (isDuplicateRequestError(err)) {
        // MVP-11 #33: the 409 already carries the pre-existing request's true, reconciled status
        // (server's `mapForGuest()` — see `requestErrors.ts`'s doc comment on `describeRequestError`'s
        // DUPLICATE_REQUEST branch), so the queue-position card can flip to match immediately
        // instead of only after the background refetch below lands.
        if (err instanceof GuestApiError && err.data) {
          setActiveRequest(err.data);
          setActiveRequestFetchedAt(Date.now());
        }
        scheduleMineRefetch();
      }
    } finally {
      setSubmitting(false);
    }
  }

  // MVP-483: the veil's retry. `loadSession()` is the right call to repeat — it is the one request
  // every branch of this page depends on, and it swallows its own failures, so success is read off
  // `getReachability()` afterwards (which `guestFetch` has just set either way) rather than from a
  // rejection that never comes. Deliberately NOT a resend of anything the customer submitted: there
  // is no write queue here and this button must not become one.
  async function handleOutageRetry() {
    setOutageRetrying(true);
    setOutageRetryFailed(false);
    try {
      await loadSession();
    } finally {
      setOutageRetrying(false);
    }
    setOutageRetryFailed(getReachability() === 'unreachable');
  }

  // MVP-11 #32: dismissing the stale-status card persists which request was dismissed
  // (`dismissedRequest.ts`) so it stays gone across reloads, and clears it from THIS page instance
  // right away rather than waiting on the next refetch.
  function handleDismissActiveRequest() {
    if (!activeRequest) return;
    dismissRequest(activeRequest.requestId);
    setActiveRequest(null);
    setActiveRequestFetchedAt(null);
  }

  /**
   * MVP-491 Gap 4 — the last leg of a keyboard-only request.
   *
   * This is an effect on the PAGE, deliberately, and that is the whole trick. `handleSubmit`'s
   * success batches `setSheetSong(null)` with `setActiveRequest(result)`, so in one commit the
   * sheet unmounts and this card mounts. React runs every effect CLEANUP for a commit before any
   * effect body, and the sheet's trap is a descendant of this component — so
   * `useFocusTrap.ts:86`'s restore-focus-to-the-song-row has already happened by the time this line
   * runs, and the move lands on a settled DOM instead of racing it. The same ordering fact MVP-490
   * turned on (`consoleSearchFocus.ts`), applied one level down.
   *
   * The flag is consumed whether or not it can be honoured, for the reason `clearSearchFocus`
   * exists: a request that lingers in a module/ref outlives the moment it was about and eventually
   * steals focus on some unrelated later card.
   */
  useEffect(() => {
    const pending = pendingQueueFocusRef.current;
    pendingQueueFocusRef.current = false;
    const intent = resolveQueueCardFocus({
      pending,
      hasQueueCard: queueCardRef.current !== null,
      isDialogOpen: document.querySelector('[role="dialog"]') !== null,
    });
    applyGuestNavIntent(intent, { rows: [], searchField: null, queueCard: queueCardRef.current });
  }, [activeRequest?.requestId]);

  // ---- MVP-483: the outage veil -------------------------------------------------------------
  //
  // Same vocabulary and same decision table as the Stage (`stageOutageDisplay`), fed by the signal
  // Part 0 of this task finally gave the guest route: `guestFetch` reports reachability now, so
  // `getReachability()` is no longer permanently 'unknown' on `/j/<code>`.
  //
  // The one deliberate difference from the Stage: a retry button. The Stage is a monitor pointed at
  // a room with nobody in front of it; this is a phone in a customer's hand, and the person holding
  // it has just tapped something and watched nothing happen. `Button`'s own sizing carries the 44px
  // tap floor (MVP-273/274) — that is why this reuses it rather than styling an anchor.
  //
  // Nothing is queued while the veil is up. There is no offline write queue in this app: a request
  // the customer submits during an outage fails, says so through `describeRequestError`, and is
  // theirs to send again.
  const outage = useConnectionHealth(realtimeClientInstance);
  // MVP-483 follow-up: `handleOutageRetry` clears this flag only on the way into the NEXT retry, so
  // an outage that resolves on its own — the 15s `guestRefetch` poll recovering while the customer
  // waits, which is the common case — left it set. The veil goes, the flag stays, and the next
  // outage opens with "Still no connection" before they have touched anything.
  useEffect(() => {
    if (outage.scope !== 'app') setOutageRetryFailed(false);
  }, [outage.scope]);
  const outageOverlay =
    outage.scope === 'app' ? (
      <div className="guest-outage" role="status" data-testid="guest-outage">
        <div className="guest-outage__card">
          {/* No `label`: the `<h2>` below says the same sentence, and this whole card is one
              `role="status"` region — a labelled spinner made a screen reader read it twice. */}
          <Spinner size={28} />
          <h2 className="guest-outage__title">{t('common.outage.guestTitle')}</h2>
          <p className="guest-outage__body">{t('common.outage.guestBody')}</p>
          {/* MVP-565: inline. Caption on the button below; floated it would clear its own veil
              (`--z-overlay` 300 vs `--z-toast` 500) and detach. */}
          {outageRetryFailed && <InlineAlert variant="danger">{t('common.outage.guestRetryFailed')}</InlineAlert>}
          <Button variant="primary" fullWidth loading={outageRetrying} onClick={() => void handleOutageRetry()}>
            {outageRetrying ? t('common.outage.guestRetrying') : t('common.outage.guestRetry')}
          </Button>
        </div>
      </div>
    ) : null;

  // ---- Render: gate states first (loading / not-found / closed / passcode), then the real page ----

  if (closedMessage) {
    return (
      <GuestPage overlay={outageOverlay}>
        <div className="guest-page__gate">
          <EmptyState icon="inbox" title={t('guest.closed.title')} description={closedMessage} />
        </div>
      </GuestPage>
    );
  }

  if (sessionErrorCopy) {
    return (
      <GuestPage overlay={outageOverlay}>
        <div className="guest-page__gate">
          <ErrorState
            title={t(sessionErrorCopy.titleKey, sessionErrorCopy.titleParams)}
            description={t(sessionErrorCopy.detailKey, sessionErrorCopy.detailParams)}
          />
        </div>
      </GuestPage>
    );
  }

  if (!session) {
    // P2 fix (round 2): this used to be a bare spinner with no search field in the DOM at all, so
    // a guest who started typing the instant the page painted (AC-REQ-2's own "QR scan -> search
    // box focused and interactive", tested live at ~1s post-load) had those keystrokes land on
    // nothing and vanish — there was no input to receive them, joinSession() had not resolved yet.
    // `query`/`setQuery` already live in this component (not in `GuestSearch`), so mounting the
    // real, focused field here rather than only in the "ready" return below costs nothing: once
    // `session` resolves, whatever the guest already typed is still sitting in `query` and the
    // search effect (gated on `ready`, further down) fires for it exactly as if they had waited.
    //
    // P2 fix (round 3): that round-2 fix alone was not enough — confirmed live, typing right after
    // load and letting `session` resolve mid-keystroke left the field EMPTY. This element is the
    // sole child here (array index 0) but sits at a LATER index below (after the venue/message
    // slots), so React's unkeyed reconciliation saw a different element type at index 0 and threw
    // the whole subtree away rather than reusing it, taking focus (and, mid-keystroke, characters)
    // with it. The explicit `key` below (matched by the same key on the other instance) tells React
    // this is the SAME logical field across that transition, so it moves/updates it in place instead.
    return (
      <GuestPage overlay={outageOverlay}>
        <GuestSearch
          key="guest-search"
          mode={mode}
          onModeChange={setMode}
          query={query}
          onQueryChange={setQuery}
          code={code}
          onCodeChange={setCode}
          songs={[]}
          loading={true}
          errorMessage={null}
          onPick={() => {}}
        />
      </GuestPage>
    );
  }

  const venueLabel = [session.venue, session.name].filter(Boolean).join(' · ');
  const hintKey = guestConnectionHint(connectionState);

  // MVP-471: the bot challenge, ahead of the passcode gate. Order matters: the passcode is the
  // venue's own PIN and the challenge is about whether there is a person here at all, and a
  // session that asks for both should ask the cheaper, non-secret question first — a bot should
  // not get to spend a room's wrong-PIN budget before proving it is a phone.
  if (needsChallenge) {
    return (
      <GuestPage overlay={outageOverlay}>
        {venueLabel && <div className="guest-page__venue text-meta truncate">{venueLabel}</div>}
        <div className="guest-page__gate">
          <EmptyState
            icon="info"
            title={t('guest.gate.challengeTitle')}
            description={t('guest.gate.challengeDescription')}
            action={
              challengeUnavailable ? (
                <InlineAlert variant="danger">{t('guest.gate.challengeUnavailable')}</InlineAlert>
              ) : (
                <div className="guest-page__gate-form">
                  {session.turnstile.siteKey && (
                    <TurnstileWidget
                      siteKey={session.turnstile.siteKey}
                      onToken={(token) => void handleChallengeToken(token)}
                      onUnavailable={() => setChallengeUnavailable(true)}
                    />
                  )}
                  {challengeSubmitting && <p className="text-meta">{t('guest.gate.challengeChecking')}</p>}
                </div>
              )
            }
          />
        </div>
      </GuestPage>
    );
  }

  if (session.passcodeRequired && !session.passcodeOk) {
    return (
      <GuestPage overlay={outageOverlay}>
        {venueLabel && <div className="guest-page__venue text-meta truncate">{venueLabel}</div>}
        <div className="guest-page__gate">
          <EmptyState
            icon="info"
            title={t('guest.gate.passcodeTitle')}
            description={session.message ?? t('guest.gate.passcodeFallbackDescription')}
            action={
              <form className="guest-page__gate-form" onSubmit={handlePasscodeSubmit}>
                <TextField
                  label={t('guest.gate.passcodeLabel')}
                  value={passcode}
                  onChange={(e) => setPasscode(e.currentTarget.value)}
                  autoFocus
                  autoComplete="off"
                />
                {passcodeBlocked ? (
                  <InlineAlert variant="danger">{t('server.rateLimited')}</InlineAlert>
                ) : (
                  passcodeTried && !session.passcodeOk && <InlineAlert variant="danger">{t('guest.gate.wrongPasscode')}</InlineAlert>
                )}
                {/* Deliberately still enabled while blocked: the budget frees itself on a timer and
                    nothing on this page knows when, so disabling the button would be a dead end
                    with no way out but a reload. Retrying just gets the same 429 back. */}
                <Button type="submit" variant="primary" fullWidth loading={passcodeSubmitting} disabled={!passcode.trim() || passcodeSubmitting}>
                  {t('guest.gate.continue')}
                </Button>
              </form>
            }
          />
        </div>
      </GuestPage>
    );
  }

  if (session.requestMode === 'closed') {
    return (
      <GuestPage overlay={outageOverlay}>
        {venueLabel && <div className="guest-page__venue text-meta truncate">{venueLabel}</div>}
        <div className="guest-page__gate">
          <EmptyState icon="inbox" title={t('guest.closed.title')} description={session.message ?? t('guest.closed.fallbackDescription')} />
        </div>
      </GuestPage>
    );
  }

  return (
    <GuestPage overlay={outageOverlay}>
      {/* MVP-497: the topline renders even with no venue name. The button is the only pointer way
          back out of fullscreen on this surface — ESC and F11 are the others, and neither is
          something a customer holding a phone-shaped page on a room PC knows to reach for — so it
          cannot depend on whether the venue filled in a name. Absent, not disabled, where the
          browser forbids fullscreen (see `LibraryScreen.tsx` for the same call). */}
      <div className="guest-page__topline">
        <span className="guest-page__venue text-meta truncate">{venueLabel}</span>
        {fullscreen.supported && (
          <IconButton
            icon={fullscreen.isFullscreen ? 'minimize' : 'maximize'}
            label={t(fullscreen.isFullscreen ? 'common.fullscreen.exit' : 'common.fullscreen.enter')}
            size="lg"
            active={fullscreen.isFullscreen}
            onClick={fullscreen.toggle}
          />
        )}
      </div>
      {/* MVP-498 slice 2, owner decision 3: one line, while the stream is rebuilt onto the new room.
          Not an error and not a veil — nothing is broken and nothing is lost, so it sits inline above
          the KJ's own message rather than covering the page. Clears itself on the new connection's
          `hello` frame. */}
      {roomMoved && (
        <div className="guest-page__message">
          <InlineAlert variant="info">{t('guest.roomMoved.notice')}</InlineAlert>
        </div>
      )}
      {session.message && (
        <div className="guest-page__message">
          <InlineAlert variant="info">{session.message}</InlineAlert>
        </div>
      )}

      <GuestSearch
        key="guest-search"
        mode={mode}
        onModeChange={setMode}
        query={query}
        onQueryChange={setQuery}
        code={code}
        onCodeChange={setCode}
        songs={songs}
        loading={searchLoading}
        errorMessage={searchErrorMessage}
        onDismissError={() => setSearchErrorMessage(null)}
        onPick={openSheetFor}
        browsing={browsing}
        hasMore={browseHasMore}
        loadingMore={browseLoadingMore}
        onLoadMore={loadMoreBrowse}
        moreError={browseMoreError}
      />

      {activeRequest && (
        <QueuePositionCard
          request={activeRequest}
          fetchedAt={activeRequestFetchedAt ?? undefined}
          // MVP-272: `guestConnectionHint` returns the KEY of DESIGN §6.4's shared connection
          // vocabulary (it lives in a pure module with no locale of its own) — rendering it is this
          // page's job, and the card keeps taking finished text.
          connectionHint={hintKey ? t(hintKey) : null}
          onDismiss={handleDismissActiveRequest}
          containerRef={queueCardRef}
        />
      )}

      <RequestSheet
        open={sheetSong !== null}
        song={sheetSong}
        singerName={singerName}
        onSingerNameChange={setSingerName}
        keyShift={keyShift}
        onKeyShiftChange={setKeyShift}
        submitting={submitting}
        errorCopy={submitErrorCopy}
        onDismissError={() => setSubmitErrorCopy(null)}
        onSubmit={() => void handleSubmit()}
        onClose={closeSheet}
      />
    </GuestPage>
  );
}
